import { useState, useEffect, useMemo, useCallback } from 'react'
import { generateVideoSuggestions } from '../../../../../services/api/aiVideoGeneration'
import useTaskPoller from '../../../hooks/useTaskPoller'
import { buildVideoSuggestionsPayload } from './videoSuggestionsPayload'

const DEFAULT_ERROR_MESSAGE =
  'Unable to generate AI review suggestions for this video right now.'

const isStoredReviewValid = (storedReview, videoUrl, videoId) =>
  Boolean(
    storedReview &&
      storedReview.data &&
      storedReview.videoUrl === videoUrl &&
      storedReview.videoId === videoId
  )

const useVideoSuggestions = ({
  visible,
  videoUrl,
  shotList,
  videoId,
  storedReview,
  setFormData
}) => {
  const [suggestions, setSuggestions] = useState(null)
  const [isLocalLoading, setIsLocalLoading] = useState(false)
  const [error, setError] = useState('')
  const [hasStartedReview, setHasStartedReview] = useState(false)

  const {
    resolveTaskData,
    isPolling: isTaskPolling,
    progress,
    status,
    message,
    stopPolling
  } = useTaskPoller({ pollInterval: 3000 })

  const suggestionRequest = useMemo(
    () => buildVideoSuggestionsPayload(videoUrl, shotList, videoId),
    [videoUrl, shotList, videoId]
  )

  useEffect(() => {
    if (!visible) return

    if (isStoredReviewValid(storedReview, videoUrl, videoId)) {
      setSuggestions(storedReview.data)
      setHasStartedReview(true)
      setError('')
      return
    }

    setSuggestions(null)
    setHasStartedReview(false)
  }, [visible, storedReview, videoUrl, videoId])

  const fetchSuggestions = useCallback(async () => {
    if (!suggestionRequest.payload) return

    try {
      setIsLocalLoading(true)
      setError('')
      const response = await generateVideoSuggestions(suggestionRequest.payload)
      const result = await resolveTaskData(response)
      const nextSuggestions = result?.output || result?.data || result || null

      setSuggestions(nextSuggestions)

      if (setFormData && nextSuggestions) {
        setFormData(prev => ({
          ...prev,
          videoReview: {
            data: nextSuggestions,
            videoUrl,
            videoId,
            reviewedAt: new Date().toISOString()
          }
        }))
      }
    } catch (err) {
      console.error('Failed to fetch video suggestions:', err)
      setSuggestions(null)
      setError(
        err?.response?.data?.message || err?.message || DEFAULT_ERROR_MESSAGE
      )
    } finally {
      setIsLocalLoading(false)
    }
  }, [suggestionRequest, resolveTaskData])

  useEffect(() => {
    if (!visible) {
      stopPolling()
      setHasStartedReview(false)
      setSuggestions(null)
      setError('')
      return
    }

    if (!suggestionRequest.payload || !hasStartedReview) {
      return
    }

    if (isStoredReviewValid(storedReview, videoUrl, videoId)) {
      return
    }

    fetchSuggestions()
  }, [
    visible,
    suggestionRequest,
    fetchSuggestions,
    hasStartedReview,
    stopPolling,
    storedReview,
    videoUrl,
    videoId
  ])

  const startReview = useCallback(() => {
    setHasStartedReview(true)
  }, [])

  return {
    suggestions,
    isLoading: isLocalLoading || isTaskPolling,
    error,
    fetchSuggestions,
    suggestionRequest,
    hasStartedReview,
    startReview,
    progress,
    status,
    message
  }
}

export default useVideoSuggestions
